// =====================================================
// TUNING: every number that decides how the comparison reads two drafts, in one table.
//
// Each is a judgement about documents, not about code: change one only with the diff-quality
// benchmark (test/diff-quality.mjs) and the engine comparison over real pairs
// (test/engine-compare.mjs) in hand, and say here what it guards. Until stage 6 of the engine
// cleanup these sat as literals in diff-engine-core.ts and move-detection.ts. Limits that only
// trade speed for memory and do not change the result (the word diff's small-input limit and
// D_LIMIT: it stays optimal past them, test/diff-check.mjs) stay with their code.
// Similarities are word overlaps from 0 to 1 (diff-engine-core `similarity`: twice the longest
// common sequence of words over both lengths; `cheapSimilarity`: the same over word sets).
// =====================================================

export const TUNING = {
  // ── Pairing paragraphs (diff-engine-core matchParagraphs) ──
  /** Two paragraphs at least this similar can be paired as one edited paragraph. Below it, one
   *  is deleted and the other inserted. */
  MATCH_THRESHOLD: 0.25,
  /** A pair whose word sets overlap less than this is taken as that dissimilar, without the exact
   *  (slower) similarity: it is far below MATCH_THRESHOLD either way. */
  MATCH_PREFILTER: 0.15,
  /** The alignment only considers pairings near the diagonal: at least this many paragraphs either
   *  side of it, */
  MATCH_BAND_MIN: 20,
  /** … or this share of the longer draft, whichever is more. A block moved further than that is
   *  found by move detection instead. */
  MATCH_BAND_SHARE: 0.15,

  // ── An edited paragraph shown whole (diff-engine-core diffDocuments) ──
  /** A paired paragraph less similar than this is shown as the old one deleted and the new one
   *  inserted, not as a word-by-word redline (lowered from 0.15 to keep more word-level
   *  redlines, as Word does) … */
  UNBUNDLE_BELOW: 0.08,
  /** … when both have at least this many words (a short line stays word by word). */
  UNBUNDLE_MIN_WORDS: 4,

  // ── An edit inside a word (diff-engine-core subWordSplit) ──
  /** A replaced word is shown as an edit inside it ("Declaration" → "Declarations") only when both
   *  words have at least this many characters, */
  SUBWORD_MIN_LENGTH: 2,
  /** … their shared start and end cover at least this share of the shorter word, */
  SUBWORD_SHARED_SHARE: 0.5,
  /** … and the changed middle is empty on one side, or at most this many characters on both
   *  ("(30)" → "(60)"); a larger middle ("Property" → "Premises") is a different word. */
  SUBWORD_MAX_SWAP: 2,

  // ── Paragraph splits and merges (diff-engine-core restructureSplitsAndMerges) ──
  /** A split or merge is found among at most this many pieces, */
  RESTRUCTURE_MAX_PIECES: 6,
  /** … within a window of this many consecutive paragraphs of the comparison, */
  RESTRUCTURE_WINDOW: 12,
  /** … holding at most this many edited paragraphs. */
  RESTRUCTURE_MAX_MODIFIES: 3,
  /** A split or merge that also edits a few words: at most this many words changed, */
  NEAR_MAX_CHANGES: 4,
  /** … and never more than this share of the paragraph's words (a quarter), */
  NEAR_MAX_SHARE: 4,
  /** … in a paragraph of at least this many words, */
  NEAR_MIN_WORDS: 8,
  /** … whose pieces' words, taken together, overlap the whole this much. */
  NEAR_SIMILARITY: 0.8,

  // ── Moved paragraphs (move-detection detectMoves) ──
  /** A deleted and an inserted paragraph at least this similar are one paragraph moved. */
  MOVE_THRESHOLD: 0.50,
  /** Above this similarity a move is shown as a clean moved block; below it, the edits made in
   *  transit are shown inside the moved text. */
  MOVE_CLEAN_THRESHOLD: 0.97,
  /** A paragraph needs at least this many words to be a move candidate (shorter ones move only
   *  when unique: MOVE_SHORT_*). */
  MIN_MOVE_WORDS: 4,
  /** Candidates whose lengths differ by more than this factor are not compared. */
  MOVE_LENGTH_RATIO: 4,
  /** Candidates whose word sets overlap less than this are not compared exactly. */
  MOVE_PREFILTER: 0.20,
  /** A short paragraph deleted in one place and inserted, exactly, in one other place (and found
   *  nowhere else) is a move when it has at least this many words … */
  MOVE_SHORT_MIN_WORDS: 2,
  /** … or this many characters. */
  MOVE_SHORT_MIN_CHARS: 6,

  // ── Moved clauses inside paragraphs (move-detection detectSubParagraphMoves) ──
  /** A deleted run of words and an inserted run elsewhere are one clause moved when each has at
   *  least this many words … */
  SUBMOVE_MIN_WORDS: 8,
  /** … and they are at least this similar (conservative: boilerplate phrases must not move). */
  SUBMOVE_THRESHOLD: 0.85,
  /** Runs whose lengths differ by more than this factor (below 0.33 or above 3) are not compared. */
  SUBMOVE_LENGTH_RATIO: 3,
  SUBMOVE_LENGTH_RATIO_MIN: 0.33,
  /** Runs whose word sets overlap less than this are not compared exactly. */
  SUBMOVE_PREFILTER: 0.40,
} as const;
